import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Skeleton, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import type {
  ConversationRecap,
  ConversationTurn,
  Correction,
  CorrectionAction,
  Gloss,
} from '@nelnela/contracts';
import { useStudy } from '../app/study';
import { api, ApiError } from '../lib/api';
import { instantLookup, settle } from '../lib/instant';
import {
  bubbleRuns,
  chatCalmLine,
  latestSuggestions,
  learnerTurnCount,
  optimisticLearnerTurn,
  scenarioLabel,
  sendBlock,
  sendBlockLine,
  sendBlockPlaceholder,
  sendIdentity,
  standingBlock,
  turnPlacements,
  TURN_MAX_CHARS,
  type ActivityState,
  type ChatApi,
  type PendingSend,
  type SendGate,
} from '../lib/chat';
import { editedAgo, insertSuggestion } from '../lib/journal';
import { startActivity, type ActivityHandle } from '../lib/session';
import { ulid } from '../lib/ulid';
import { useLiveProfile } from '../lib/use-profile';
import { Bubble, PendingBubble } from '../components/chat/Bubble';
import { KeyboardBox } from '../components/KeyboardBox';
import { RecapPanel } from '../components/chat/Recap';
import { ScenarioSheet } from '../components/chat/ScenarioSheet';
import { GlossDrawer, type GlossSheetState } from '../components/watch/GlossSheet';
import { IconBack } from '../components/watch/icons';
import { CorrectionCard } from '../components/CorrectionCard';
import { TRANSCRIPT_SIZE_DEFAULT } from './me-settings';

/**
 * F13 Text chat — a conversation you can have badly (design `8e`, `12e`).
 *
 * Two screens: `/chat` is the scenario sheet plus the history, `/chat/:conversationId` is the run.
 * **`/dialogue` is not this screen's to claim** — `Practice.tsx` has linked there since M6a and
 * M6d builds what is behind it. Text chat is `/chat`, and *dialogue* stays reserved for the spoken
 * feature.
 *
 * **What this screen does NOT rebuild, because M6c-i built it and chat inherits it proven:** the
 * correction card (`components/CorrectionCard`, shared with the journal), the underline and its error
 * tint, the `correctionAct` call, the caret-preserving chip insert (`insertSuggestion`), and every
 * line of span arithmetic (`reanchor`, `dropOverlaps`, through `lib/chat.ts`). The one thing that
 * differs is what an acted-on correction looks like afterwards, and `turnPlacements` says why.
 *
 * **The wait is a shimmer, never a spinner.** `ConversationTurnResponse` returns BOTH turns in one
 * response and there is no streaming, so the learner's own words go up optimistically the moment
 * they send and the app's side is `.nn-shimmer` — already a global class, since `READER_CSS` is
 * concatenated into `App.tsx`'s one `<style>` element.
 */

/**
 * `lib/api.ts`'s chat methods, against the interface this screen was built to.
 *
 * The assignment below is the guard, and it is not decoration: `api` is a hand-written object
 * literal with ninety-odd methods and no generated types, so a rename on that side would otherwise
 * be caught by nothing until this screen threw "conversationTurn is not a function" in a browser.
 * The screen was built against a cast while `lib/api.ts` was another step's file; the cast is gone
 * and this is what replaced it, exactly as `Journal.tsx` records for its own.
 */
const chatApi: ChatApi = api;

// ---------------------------------------------------------------------------------------------
// /chat — the scenario sheet and the history
// ---------------------------------------------------------------------------------------------

export function Chat() {
  const study = useStudy();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const profile = study.me.profiles[0] ?? null;
  const capabilities = profile ? study.me.capabilities[profile.targetLang] : undefined;

  /**
   * The sheet opens on arrival, and the DoD is why: "Practice → Text chat opens the scenario
   * sheet". Someone who tapped a tile called Text chat has already decided to chat; the next
   * decision is which scenario, and a "New chat" button in front of it is a tap for nothing.
   *
   * It does NOT open when the run screen sent us back here — that navigation carries
   * `state.picked`, and a sheet in the face of someone who just finished a conversation is the
   * opposite of what they asked for. `location.state` rather than a module flag so a reload
   * behaves like a fresh arrival, which it is.
   */
  const returned = (location.state as { picked?: boolean } | null)?.picked === true;
  const [sheet, setSheet] = useState(!returned);
  const [busy, setBusy] = useState(false);
  const [calm, setCalm] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['conversations'],
    queryFn: () => chatApi.conversationList({ limit: 30 }),
  });

  const start = useCallback(
    async (scenario: string) => {
      if (!profile) return;
      setBusy(true);
      setCalm(null);
      /**
       * The conversation is opened here and the run screen is navigated to with its id, which is
       * `Journal`'s shape exactly (`compositionCreate` → `/journal/:id`) and keeps
       * `/chat/:conversationId` meaning "a conversation that exists".
       *
       * The activity is the cost of that consistency and it is stated rather than hidden: the app
       * speaks first, so `conversationStart` makes a model call and needs an `activityId`, and
       * there is no conversation id to hang an activity on until it returns. So this short-lived
       * `chat` activity carries the opening turn, and the run screen opens its own with
       * `{conversationId}` for every turn after it. `conversationScenario` is the contract's own
       * key for this (`DailyBite.plan[].target`), so the first activity still says what it was for.
       */
      let handle: ActivityHandle | null = null;
      try {
        handle = await startActivity(study.session, 'chat', { conversationScenario: scenario });
        const conv = await chatApi.conversationStart({
          targetLang: profile.targetLang,
          baseLang: profile.baseLang,
          mode: 'text_chat',
          scenario,
          activityId: handle.id,
        });
        // Seeded so the run screen renders the opening turn without a second round trip; it still
        // refetches, and react-query's structural sharing makes an unchanged answer free.
        queryClient.setQueryData(['conversation', conv.id], conv);
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
        setSheet(false);
        navigate(`/chat/${conv.id}`);
      } catch (err) {
        setBusy(false);
        setCalm(chatCalmLine(err, 'The conversation didn’t open. Try again in a moment.'));
      } finally {
        if (handle) void handle.end();
      }
    },
    [navigate, profile, queryClient, study.session],
  );

  const items = list.data?.items ?? null;

  return (
    <Container size={640} py="lg">
      <Stack gap="md">
        <Title order={2}>Text chat</Title>

        <div style={panel}>
          <div style={{ fontSize: 14, color: 'var(--nn-text-strong)' }}>Have a conversation</div>
          <Text fz={11.5} c="var(--nn-muted)" mt={6}>
            {/* Rule 2 of the milestone, said where it is relevant rather than in a policy page. */}
            In Georgian, in a situation you pick. Mistakes get a soft underline as you go — the
            conversation never stops to correct you. What you say here stays yours.
          </Text>
          <div style={{ marginTop: 11, display: 'flex', gap: 10, alignItems: 'center' }}>
            <UnstyledButton
              disabled={busy || !study.entitled}
              onClick={() => setSheet(true)}
              style={{ ...emberButton, opacity: busy || !study.entitled ? 0.5 : 1 }}
            >
              {busy ? 'Opening…' : 'New chat'}
            </UnstyledButton>
            {/* `conversationList` is `session`, not `entitled`: a learner whose trial has ended can
                still read every conversation they have had. Only starting another is gated, so
                only the button is. */}
            {!study.entitled && (
              <span style={{ fontSize: 11, color: 'var(--nn-faint)' }}>
                Chat is part of the paid layer — what you said is still here.
              </span>
            )}
          </div>
        </div>

        {list.isPending ? (
          <Stack gap={8}>
            <Skeleton height={44} radius="sm" />
            <Skeleton height={44} radius="sm" />
          </Stack>
        ) : list.isError || !items || items.length === 0 ? (
          <Text fz={12} c="var(--nn-faint)">
            {list.isError
              ? 'Your conversations will be here when the connection is.'
              : 'No conversations yet.'}
          </Text>
        ) : (
          <Stack gap={8}>
            {items.map((c) => (
              <UnstyledButton key={c.id} component={Link} to={`/chat/${c.id}`} style={listRow}>
                <div style={{ fontSize: 13.5, color: 'var(--nn-text-strong)' }}>
                  {scenarioLabel(c.scenario)}
                </div>
                <div style={{ fontSize: 10.5, color: 'var(--nn-faint)', marginTop: 3 }}>
                  {c.turns} {c.turns === 1 ? 'turn' : 'turns'} · {editedAgo(c.startedAt)}
                  {c.endedAt === null && ' · still open'}
                </div>
              </UnstyledButton>
            ))}
          </Stack>
        )}
      </Stack>

      <ScenarioSheet
        opened={sheet}
        onClose={() => setSheet(false)}
        onPick={(s) => void start(s)}
        entitled={study.entitled}
        busy={busy}
        spokenDialogue={capabilities?.spokenDialogue === true}
        onSpoken={() => navigate('/dialogue')}
        calm={calm}
      />
    </Container>
  );
}

// ---------------------------------------------------------------------------------------------
// /chat/:conversationId — the run
// ---------------------------------------------------------------------------------------------

/** The largest `latencyMs` the schema accepts; a tab left open overnight must not 422. */
const MAX_LATENCY_MS = 3_600_000;

export function ChatRun() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const study = useStudy();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const profile = useLiveProfile(study.me.profiles[0] ?? null);
  const fontSizePx = profile?.display?.transcriptSize ?? TRANSCRIPT_SIZE_DEFAULT;

  const convQ = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => chatApi.conversationGet(conversationId!),
    enabled: !!conversationId,
  });

  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [pending, setPending] = useState<ConversationTurn | null>(null);
  const [composer, setComposer] = useState('');
  const [caret, setCaret] = useState(0);
  const [acted, setActed] = useState<ReadonlyMap<string, CorrectionAction>>(() => new Map());
  const [card, setCard] = useState<Correction | null>(null);
  const [calm, setCalm] = useState<string | null>(null);
  const [ended, setEnded] = useState(false);
  /**
   * A 429 stops the composer for this visit, and it is deliberately NOT `ended`.
   *
   * The two refusals that answer 429 are "this conversation has run long" and "the AI helper is
   * resting" — the first is about this conversation and the second is about the day's budget — and
   * neither of them has ended anything. Folding them into `ended` would have the placeholder tell
   * a learner whose month is spent that their conversation is over, which is not true and which
   * they cannot argue with. The server's own sentence carries the reason; this only stops the
   * client from spending another request that is guaranteed to be refused.
   */
  const [paused, setPaused] = useState(false);
  const [recap, setRecap] = useState<ConversationRecap | null>(null);
  const [ending, setEnding] = useState(false);

  const seededRef = useRef(false);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  /** when the app's last turn landed — `latencyMs` is measured from it, which is the only reading
   *  of "how long did the learner take" that a text turn supports. */
  const lastTurnAtRef = useRef(Date.now());
  /**
   * The send that failed, kept whole: its text, its ULID, and whether the app handed the text over.
   *
   * `conversationTurn` is idempotent on the client's `eventId`, so a retry of the SAME message must
   * carry the SAME id or a network error that actually landed would produce two exchanges. It must
   * also carry the same `suggestedTaken`, which it did not: a failed chip put its own text back in
   * the composer and Send re-sent it as unprompted production. `sendIdentity` holds both rules.
   * Cleared on success and whenever the learner sends something else.
   */
  const retryRef = useRef<PendingSend | null>(null);
  /**
   * The gate, as of the last render, for `send` to re-check.
   *
   * A ref rather than `send`'s dependency list, which is `caretRef`'s reasoning one section down:
   * the chips call `send` with their own text, so the check has to happen inside `send` and not
   * only in the button's `disabled`. Without it the two controls contradicted each other — the
   * Send button was dimmed at the turn cap while a chip beside it still sent.
   */
  const gateRef = useRef<SendGate>({
    draft: '',
    inFlight: false,
    learnerTurns: 0,
    ended: false,
    activity: 'starting',
  });

  // ------------------------------------------------ what the server already knows
  useEffect(() => {
    const data = convQ.data;
    if (seededRef.current || !data) return;
    seededRef.current = true;
    setTurns(data.turns);
    setEnded(data.endedAt !== null);
  }, [convQ.data]);

  // ------------------------------------------------ the activity
  // Every turn's cost is ledgered against it and every `production_use` event hangs off it, so the
  // run opens one `chat` activity for the visit exactly as the journal opens one per entry.
  const activityRef = useRef<ActivityHandle | null>(null);
  /**
   * …and the same fact as state, because the composer has to be able to say it.
   *
   * The bug this replaces: the catch below only warned to the console, and `send` returned
   * silently when `activityRef.current` was empty. `startActivity` throws immediately on a
   * permanent 4xx and after ten tries otherwise, so a learner could land on a chat where Send,
   * Enter and every reply chip did literally nothing — forever, with the box live and the button
   * un-dimmed, a state the screen could neither leave nor explain.
   */
  const [activity, setActivity] = useState<ActivityState>('starting');
  useEffect(() => {
    if (!conversationId) return;
    let cancelled = false;
    let handle: ActivityHandle | null = null;
    setActivity('starting');
    startActivity(study.session, 'chat', { conversationId })
      .then((a) => {
        if (cancelled) {
          void a.end();
          return;
        }
        handle = a;
        activityRef.current = a;
        setActivity('ready');
      })
      .catch(() => {
        console.warn('[chat] activityStart failed; a turn has nothing to hang on');
        if (!cancelled) setActivity('failed');
      });
    return () => {
      cancelled = true;
      activityRef.current = null;
      if (handle) void handle.end();
    };
  }, [conversationId, study.session]);
  useEffect(() => {
    const onHide = () => {
      void activityRef.current?.end({ keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // ------------------------------------------------ the newest turn is the one being read
  // Instant, not smooth: a chat that animates its way down on every turn is a chat you cannot
  // read while it is still moving, and the learner's own message has just left their hands.
  useEffect(() => {
    const box = scrollRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [turns.length, pending, card]);

  // ------------------------------------------------ sending
  const send = useCallback(
    async (raw: string, fromChip: boolean) => {
      const draft = raw.trim();
      /**
       * Every reason this send cannot go, in one place and with a sentence attached.
       *
       * The chips call `send` directly, so this is the only check both controls pass through —
       * and the previous code had no check here at all beyond "is there an activity", which is how
       * a chip could send past a cap that had already dimmed the Send button beside it. `empty`
       * and `in_flight` stay silent because they explain themselves: an empty box, and a shimmer
       * already on screen.
       */
      const block = sendBlock({ ...gateRef.current, draft });
      if (!conversationId || block === 'empty' || block === 'in_flight') return;
      if (block !== null) {
        const say = sendBlockLine(block);
        // Only when there IS a line. `ended` has none of its own — the bar and the placeholder
        // both say it — and `setCalm(null)` here would wipe the 429 sentence that put the composer
        // to sleep in the first place, which is the learner's only explanation for it.
        if (say !== null) {
          setCard(null);
          setCalm(say);
        }
        return;
      }
      const activityId = activityRef.current?.id;
      if (!activityId) {
        // Unreachable: `activity === 'ready'` is set in the one place the handle is stored. Kept,
        // and kept LOUD, because a silent return on this exact line is the bug above.
        setCalm(sendBlockLine('activity_failed'));
        return;
      }
      // A retry of the same text keeps the failed send's id AND its hint flag; see `sendIdentity`.
      const id = sendIdentity(retryRef.current, draft, fromChip, ulid());
      retryRef.current = id;
      setCard(null);
      setCalm(null);
      setComposer('');
      setPending(optimisticLearnerTurn(draft, id.fromChip));
      try {
        const res = await chatApi.conversationTurn(conversationId, {
          activityId,
          text: draft,
          ...(id.fromChip ? { suggestedTaken: true } : {}),
          latencyMs: Math.min(MAX_LATENCY_MS, Math.max(0, Date.now() - lastTurnAtRef.current)),
          eventId: id.eventId,
        });
        setTurns((prev) => [...prev, res.learnerTurn, res.appTurn]);
        retryRef.current = null;
        lastTurnAtRef.current = Date.now();
        void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      } catch (err) {
        // The message goes back in the box rather than being lost — and only if the learner has
        // not already started typing something else, which is the one case where restoring it
        // would overwrite their work.
        setComposer((c) => (c.trim().length === 0 ? draft : c));
        setCalm(
          chatCalmLine(err, 'That didn’t reach us — your message is back in the box, try again.'),
        );
        if (err instanceof ApiError && (err.problem.status === 429 || err.problem.status === 403)) {
          setPaused(true);
        }
      } finally {
        setPending(null);
      }
    },
    [conversationId, queryClient],
  );

  // ------------------------------------------------ ending it
  const end = useCallback(async () => {
    if (!conversationId || ending) return;
    setEnding(true);
    try {
      const done = await chatApi.conversationEnd(conversationId);
      setRecap(done.recap);
      setEnded(true);
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
    } catch (err) {
      setCalm(chatCalmLine(err, 'The conversation didn’t close. It is saved either way.'));
    } finally {
      setEnding(false);
    }
  }, [conversationId, ending, queryClient]);

  // ------------------------------------------------ the correction card
  // Read by `intoComposer`, which runs from a button and must not close over a stale caret.
  const caretRef = useRef(caret);
  caretRef.current = caret;

  const act = useCallback((id: string, action: CorrectionAction) => {
    // Fire and forget, idempotent on the client's ULID — the journal's own reasoning: the
    // learner's screen has already moved, a failed `correctionAct` costs one row in the event log
    // and nothing they can see, and blocking the button on it would make the card feel broken on
    // a slow connection.
    setActed((prev) => {
      const next = new Map(prev);
      next.set(id, action);
      return next;
    });
    void chatApi
      .correctionAct(id, { action, eventId: ulid() })
      .catch(() => console.warn('[chat] correctionAct failed'));
  }, []);

  /**
   * Accept and Edit on a SENT turn.
   *
   * The journal's Accept rewrites the span in the draft and its Edit puts the caret at the end of
   * the wrong word so the learner can change its ending — which in Georgian is where the mistake
   * usually is. Neither is possible here: a turn is sent, and the app does not get to edit
   * something the learner has already said. So both actions do the one thing that is still true
   * about a correction on a sent message — they put it in the composer, where the next turn is
   * being written. Accept puts the fixed form in; Edit puts the learner's own form in, caret after
   * it, which is the same "change the ending yourself" gesture one message later.
   *
   * `insertSuggestion` is the journal's, not a copy: a phrase dropped at a caret needs a space on
   * whichever side is up against a word, and getting that wrong fuses two words into one that then
   * fails to tag and never reaches an event.
   */
  const intoComposer = useCallback((text: string) => {
    setComposer((prev) => {
      const edit = insertSuggestion(prev, Math.min(caretRef.current, prev.length), text);
      caretRef.current = edit.caret;
      return edit.text;
    });
    const el = composerRef.current;
    if (el) {
      el.focus();
      // After React has written the new value; the caret is meaningless before then.
      requestAnimationFrame(() => el.setSelectionRange(caretRef.current, caretRef.current));
    }
  }, []);

  // ------------------------------------------------ the gloss sheet
  const [glossState, setGlossState] = useState<GlossSheetState | null>(null);
  const [glossOpen, setGlossOpen] = useState(false);
  const glossTokenRef = useRef(0);
  const glossMemoRef = useRef(new Map<string, Gloss>());

  const lookup = useCallback(
    (turn: ConversationTurn, word: { surface: string; charStart: number; charEnd: number }) => {
      if (!profile) return;
      const base = {
        selection: word.surface,
        sentence: turn.text,
        // The sheet's own memo key and nothing else — `GlossSheet` uses `lineId` for
        // `${lineId}\0${selection}` and for no other purpose, so a turn id is honest here. It is
        // deliberately NOT sent on the wire; see the request below.
        lineId: turn.id,
        sel: { charStart: word.charStart, charEnd: word.charEnd },
        mode: 'tap' as const,
      };
      setGlossOpen(true);
      if (!study.entitled) {
        setGlossState({ status: 'unentitled', ...base });
        return;
      }
      const tk = ++glossTokenRef.current;
      const memoKey = `${word.surface}\u0000${turn.text}`;
      const memo = glossMemoRef.current.get(memoKey);
      setGlossState(
        memo ? { status: 'done', ...base, gloss: memo } : { status: 'loading', ...base },
      );
      // M10-i i-1: the instant layer, in flight beside the gloss. Nothing waits on it, and the
      // same `glossTokenRef` guards the race a second tap creates.
      // `settle` against `memo`: on a session-memo hit the gloss request short-circuits, so this
      // handler is the only one that runs and must do the reconcile itself.
      instantLookup(word.surface, turn.text)
        .then((inst) => settle(inst, memo))
        .then((inst) => {
          if (glossTokenRef.current !== tk) return;
          setGlossState((prev) => (prev ? { ...prev, instant: inst } : prev));
        });
      api
        .gloss({
          targetLang: profile.targetLang,
          baseLang: profile.baseLang,
          selection: word.surface,
          sentence: turn.text,
          context: {
            sessionId: study.session.id,
            ...(activityRef.current ? { activityId: activityRef.current.id } : {}),
            selectionMode: 'tap' as const,
            // The bubble advertises no weak-word dots at all, so nothing prompted this tap and
            // the log should say so rather than leave the field out (M4a principle 6).
            marked: false,
            // NOTE — the one attribution this screen cannot make. `GlossRequest.context` has
            // `lineId`, `storyId` and `sentenceIdx` and no `conversationTurnId`, and there is no
            // `conversation_turn_item` table for one to key against, so a tap in a bubble resolves
            // through `resolveEventsStep`'s `lemma_cache` fallback rather than to an exact
            // occurrence. That is a real answer, not a broken one — the same word twice in one
            // chat is the same item — but it is less precise than a reader tap, and passing the
            // turn id as `lineId` to fake it would put a claim in the event log that is false.
          },
        })
        .then(async (gloss) => {
          glossMemoRef.current.set(memoKey, gloss);
          if (glossTokenRef.current !== tk || memo) return;
          // The dictionary facts survive the gloss unless the model disagrees about the LEMMA,
          // in which case they were about the wrong word — see lib/instant.ts `reconcile`.
          const settled = await settle(await instantLookup(word.surface, turn.text), gloss);
          if (glossTokenRef.current !== tk) return;
          setGlossState({ status: 'done', ...base, instant: settled, gloss });
        })
        .catch((err: unknown) => {
          if (glossTokenRef.current !== tk || memo) return;
          if (err instanceof ApiError && err.problem.status === 403) {
            setGlossState({ status: 'unentitled', ...base });
            return;
          }
          setGlossState({
            status: 'error',
            ...base,
            message:
              err instanceof ApiError && err.problem.detail
                ? err.problem.detail
                : 'That lookup didn’t reach us.',
          });
        });
    },
    [profile, study.entitled, study.session.id],
  );

  // ------------------------------------------------ what to draw
  const shown = useMemo(() => (pending ? [...turns, pending] : turns), [turns, pending]);
  const placements = useMemo(
    () => new Map(shown.map((t) => [t.id, turnPlacements(t, acted)])),
    [shown, acted],
  );
  /**
   * The composer's gate, computed once and shared with `send` through `gateRef`.
   *
   * `learnerTurnCount` rather than `turns.length`: the server's cap is `WHERE speaker = 'learner'`
   * and counting both sides here killed Send at learner turn 21 of the 40 the server allows —
   * silently, because `shut` did not include it. `standingBlock` is what fixes the silence: a
   * block the learner is stuck behind closes the box, names itself in the placeholder and prints
   * its line, exactly as a 429 does.
   */
  const gate: SendGate = {
    draft: composer,
    inFlight: pending !== null,
    learnerTurns: learnerTurnCount(turns),
    ended: ended || paused,
    activity,
  };
  gateRef.current = gate;
  const block = sendBlock(gate);
  const standing = standingBlock(block);
  const sendable = block === null;
  const shut = ended || paused || standing !== null;
  const chips = pending || shut ? [] : latestSuggestions(turns);
  // `calm` wins: it is about something that just happened, where the standing line is about
  // something that is simply true and will still be true after it is read.
  const line = calm ?? (standing ? sendBlockLine(standing) : null);
  const title = convQ.data ? scenarioLabel(convQ.data.scenario) : 'text chat';

  if (convQ.isPending) {
    return (
      <div style={screen}>
        <Bar title="text chat" onBack={() => navigate('/chat', { state: { picked: true } })} />
        <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {['62%', '78%', '44%'].map((w) => (
            <Skeleton key={w} height={34} width={w} radius="md" />
          ))}
        </div>
      </div>
    );
  }
  if (!convQ.data) {
    return (
      <div style={{ padding: '18px 18px 0' }}>
        <Text fz={13} c="var(--nn-muted)">
          That conversation isn’t here. It may have been deleted.
        </Text>
        <UnstyledButton
          onClick={() => navigate('/chat', { state: { picked: true } })}
          style={{ ...ghostButton, marginTop: 12, display: 'inline-flex' }}
        >
          Back to Text chat
        </UnstyledButton>
      </div>
    );
  }

  // The recap replaces the conversation rather than sitting under it: it is a report ABOUT the
  // conversation, and `Read.tsx`'s `phase === 'end'` is the same move for the same reason.
  if (recap) {
    return (
      <div style={screen}>
        <Bar title={title} onBack={() => navigate('/chat', { state: { picked: true } })} />
        <RecapPanel
          recap={recap}
          title={title}
          onDone={() => navigate('/chat', { state: { picked: true } })}
        />
      </div>
    );
  }

  return (
    <KeyboardBox style={screen}>
      <Bar
        title={title}
        onBack={() => navigate('/chat', { state: { picked: true } })}
        right={
          ended ? (
            <span style={{ fontSize: 10.5, color: 'var(--nn-faint)', padding: '0 14px' }}>
              ended
            </span>
          ) : (
            <UnstyledButton
              onClick={() => void end()}
              disabled={ending}
              style={{ fontSize: 12, color: 'var(--nn-muted)', padding: '0 14px' }}
            >
              {ending ? 'ending…' : 'End'}
            </UnstyledButton>
          )
        }
      />

      {/* One `--nn-transcript-size` for the whole scroll box, so the two speakers can never
          disagree about type and the learner's 16-18 px setting reaches both. */}
      <div
        ref={scrollRef}
        style={
          {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '10px 16px 6px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            '--nn-transcript-size': `${fontSizePx}px`,
          } as React.CSSProperties
        }
      >
        {shown.map((t) => (
          <Bubble
            key={t.id}
            turn={t}
            runs={bubbleRuns(t.text, placements.get(t.id) ?? [])}
            onWord={lookup}
            onCorrection={(turn, correctionId) => {
              const found = (turn.corrections ?? []).find((c) => c.id === correctionId);
              if (found) setCard(found);
            }}
          />
        ))}
        {pending && <PendingBubble />}
      </div>

      {line && (
        <div style={{ padding: '0 16px 8px' }} role="status">
          <Text fz={11.5} c="var(--nn-muted)">
            {line}
          </Text>
        </div>
      )}

      {/* `8e`'s caption: "tap → the F12 card". The card is `screens/Journal.tsx`'s, imported and
          not reimplemented — if this were a second card, M6c-i's step 6 was built wrong. It docks
          above the chips exactly as it docks above the journal's suggestion strip, and for the
          same reason: a popover over a bubble on a 360 px phone covers the sentence it is about. */}
      {card && (
        <CorrectionCard
          correction={card}
          onAccept={() => {
            intoComposer(card.suggested);
            act(card.id, 'accepted');
            setCard(null);
          }}
          onEdit={() => {
            intoComposer(card.original);
            act(card.id, 'edited');
            setCard(null);
          }}
          onIgnore={() => {
            act(card.id, 'ignored');
            setCard(null);
          }}
        />
      )}

      <Composer
        value={composer}
        chips={chips}
        disabled={shut}
        placeholder={
          ended
            ? 'this conversation has ended'
            : paused
              ? 'not right now — the line above says why'
              : // A standing block says what it is; everything else keeps `8e`'s live words.
                sendBlockPlaceholder(block)
        }
        canSend={sendable}
        textareaRef={composerRef}
        onChange={setComposer}
        onCaret={setCaret}
        onChip={(text) => void send(text, true)}
        onSend={() => void send(composer, false)}
      />

      <GlossDrawer
        opened={glossOpen && glossState !== null}
        side="bottom"
        insetPx={0}
        state={glossState}
        onClose={() => setGlossOpen(false)}
        resume={null}
        entitled={study.entitled}
      />
    </KeyboardBox>
  );
}

// ---------------------------------------------------------------------------------------------
// the chips and the composer
// ---------------------------------------------------------------------------------------------

/** A composer taller than this scrolls instead of eating the conversation. */
const COMPOSER_MAX_PX = 108;

export interface ComposerProps {
  value: string;
  /** `8e`'s three suggested replies — tapping one SENDS it (and records `suggestedTaken`) */
  chips: readonly string[];
  disabled: boolean;
  /** `8e`'s own words when the box is live; the screen says what is true when it is not */
  placeholder?: string | undefined;
  canSend: boolean;
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
  onChange: (text: string) => void;
  onCaret: (caret: number) => void;
  onChip: (text: string) => void;
  onSend: () => void;
}

/**
 * `8e`'s bottom two rows: three reply chips over a pill and a send button.
 *
 * **A chip SENDS; it does not fill the box.** That is the design's own caption ("tap to send, or
 * type (hintUsed logged)") and it is also the honest reading of what a chip is: the learner did
 * not compose that sentence, and letting them edit it first would blur the one distinction the
 * whole `support.hintUsed` chain rests on — a phrase you were handed is recognition, not
 * production, and the grader now caps it at `good` for exactly that reason.
 *
 * Exported so a node test can render it without a router or a query client.
 */
export function Composer({
  value,
  chips,
  disabled,
  placeholder,
  canSend: sendable,
  textareaRef,
  onChange,
  onCaret,
  onChip,
  onSend,
}: ComposerProps) {
  const ownRef = useRef<HTMLTextAreaElement | null>(null);
  const ta = textareaRef ?? ownRef;

  // A chat message is one or two sentences, so the box starts at one line and grows to about four
  // before it scrolls. Measured rather than guessed: `scrollHeight` after a reset to `auto` is the
  // only reading of "how tall is this text" that survives a wrap in Georgian.
  useEffect(() => {
    const el = ta.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_PX)}px`;
  }, [ta, value]);

  return (
    <>
      {chips.length > 0 && (
        <div className="nn-chat-chips" style={chipStrip}>
          {chips.map((c, i) => (
            <UnstyledButton
              key={`${i}:${c}`}
              lang="ka"
              onClick={() => onChip(c)}
              disabled={disabled}
              style={chip}
            >
              {c}
            </UnstyledButton>
          ))}
        </div>
      )}
      <div style={composerRow}>
        <textarea
          ref={ta}
          className="nn-chat-input"
          lang="ka"
          rows={1}
          aria-label="Your message"
          placeholder={placeholder ?? 'write in Georgian…'}
          spellCheck={false}
          disabled={disabled}
          maxLength={TURN_MAX_CHARS}
          value={value}
          onChange={(e) => {
            onChange(e.currentTarget.value);
            onCaret(e.currentTarget.selectionStart);
          }}
          onSelect={(e) => onCaret(e.currentTarget.selectionStart)}
          onClick={(e) => onCaret(e.currentTarget.selectionStart)}
          onKeyDown={(e) => {
            // Enter sends, Shift-Enter breaks the line. On a phone the on-screen Return key is a
            // newline and the button is what sends, which is why the button is never hidden.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              if (sendable) onSend();
            }
          }}
        />
        <UnstyledButton
          aria-label="Send"
          disabled={!sendable}
          onClick={onSend}
          style={{ ...sendButton, opacity: sendable ? 1 : 0.4 }}
        >
          <IconSend />
        </UnstyledButton>
      </div>
    </>
  );
}

/** `8e`'s up-arrow, at the icon set's own 1.8 px / 24-box geometry. */
function IconSend() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="12" y1="18" x2="12" y2="7" />
      <polyline points="6,12 12,6 18,12" />
    </svg>
  );
}

// ---------------------------------------------------------------------------------------------
// chrome
// ---------------------------------------------------------------------------------------------

function Bar({
  title,
  onBack,
  right,
}: {
  title: string;
  onBack: () => void;
  right?: React.ReactNode;
}) {
  return (
    <div style={bar}>
      <UnstyledButton onClick={onBack} aria-label="Back" style={barButton}>
        <IconBack />
      </UnstyledButton>
      {/* `8e`'s header exactly: the scenario, then the mode in muted weight. "text chat", never
          "dialogue" — that word is reserved for the spoken feature (milestone rule 3). */}
      <Text fz={13.5} fw={600} truncate style={{ minWidth: 0, flexShrink: 1 }}>
        {title} <span style={{ color: 'var(--nn-muted)', fontWeight: 400 }}>· text chat</span>
      </Text>
      <span style={{ flex: 1 }} />
      {right}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// shared surfaces (the same objects `Read.tsx` and `Journal.tsx` each keep locally — a screen
// importing another screen's private styles is the thing both of them declined to do)
// ---------------------------------------------------------------------------------------------

const screen: React.CSSProperties = {
  height: 'calc(100dvh - var(--app-shell-footer-offset, 0rem))',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--nn-bg)',
};

const bar: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  height: 44,
  padding: '0 4px',
  gap: 2,
  flexShrink: 0,
  minWidth: 0,
};

const barButton: React.CSSProperties = {
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--nn-text)',
  flexShrink: 0,
};

const panel: React.CSSProperties = {
  background: 'var(--nn-panel)',
  border: '1px solid var(--nn-hairline)',
  borderRadius: 12,
  padding: 16,
};

const listRow: React.CSSProperties = {
  display: 'block',
  background: 'var(--nn-panel)',
  border: '1px solid var(--nn-hairline)',
  borderRadius: 9,
  padding: '10px 13px',
};

const emberButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 14px',
  borderRadius: 7,
  background: 'var(--nn-accent)',
  color: 'var(--nn-on-accent)',
  fontSize: 12.5,
  fontWeight: 600,
};

const ghostButton: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '8px 14px',
  borderRadius: 7,
  border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
  color: 'var(--nn-text)',
  fontSize: 12.5,
};

const chipStrip: React.CSSProperties = {
  display: 'flex',
  gap: 7,
  padding: '0 14px 8px',
  overflowX: 'auto',
  flexShrink: 0,
};

const chip: React.CSSProperties = {
  fontSize: 12,
  padding: '5px 11px',
  borderRadius: 999,
  border: '1px solid color-mix(in srgb, var(--nn-text) 12%, transparent)',
  color: 'var(--nn-text)',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const composerRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 9,
  padding: '0 14px calc(14px + env(safe-area-inset-bottom))',
  flexShrink: 0,
};

const sendButton: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: '50%',
  background: 'var(--nn-accent)',
  color: 'var(--nn-on-accent)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

/**
 * The rules that cannot be inline styles, to be concatenated into `App.tsx`'s single `<style>`
 * beside `READER_CSS` and `JOURNAL_CSS`. **That wiring is the integrator's, not this step's** —
 * without it the two placeholders take the browser's default grey and the chip strip shows a
 * scrollbar on desktop. Everything load-bearing — the bubbles' geometry, the correction
 * underline — is inline for exactly that reason.
 */
export const CHAT_CSS = `
.nn-chat-input {
  flex: 1; min-width: 0; box-sizing: border-box; resize: none; overflow-y: auto;
  background: color-mix(in srgb, var(--nn-text) 4%, transparent);
  border: 1px solid var(--nn-hairline);
  border-radius: 18px; padding: 9px 14px;
  color: var(--nn-text-strong);
  font: 13px/1.5 'Noto Sans Georgian', sans-serif; outline: none;
}
.nn-chat-input::placeholder { color: var(--nn-faint); }
.nn-chat-input:focus { border-color: color-mix(in srgb, var(--nn-accent) 65%, transparent); }
.nn-chat-own {
  flex: 1; min-width: 0; box-sizing: border-box;
  background: color-mix(in srgb, var(--nn-text) 4%, transparent);
  border: 1px solid var(--nn-hairline);
  border-radius: 9px; padding: 10px 13px;
  color: var(--nn-text-strong);
  font: 13px/1.4 'Noto Sans Georgian', sans-serif; outline: none;
}
.nn-chat-own::placeholder { color: var(--nn-faint); }
.nn-chat-chips { scrollbar-width: none; }
.nn-chat-chips::-webkit-scrollbar { display: none; }
`;
